import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Plus, Users, MessageSquare, Target, ArrowRight, Calendar, BarChart3, BookOpen, Layers } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AREA_THEMES = {
  "Servicios Urbanos": { from: "#0ea5e9", to: "#0b3b22", accent: "#3BAD2D", emoji: "🛣️" },
  "Tratamiento Operaciones": { from: "#7c3aed", to: "#0b3b22", accent: "#a78bfa", emoji: "♻️" },
  "Tratamiento Privados": { from: "#f59e0b", to: "#0b3b22", accent: "#fbbf24", emoji: "📈" },
};

export default function NovaPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/nova/campaigns`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setCampaigns(Array.isArray(d) ? d : []);
        setLoading(false);
      });
  }, [token]);

  const totals = campaigns.reduce(
    (acc, c) => ({
      challenges: acc.challenges + (c.challenges_count || 0),
      invitees: acc.invitees + (c.invitees_count || 0),
      submissions: acc.submissions + (c.submissions_count || 0),
    }),
    { challenges: 0, invitees: 0, submissions: 0 }
  );

  return (
    <div className="space-y-7" data-testid="nova-page">
      {/* Hero */}
      <div
        className="relative rounded-3xl p-8 md:p-10 overflow-hidden"
        style={{
          background:
            "radial-gradient(circle at 20% 20%, rgba(59,173,45,0.25), transparent 45%), radial-gradient(circle at 90% 80%, rgba(139,92,246,0.25), transparent 50%), linear-gradient(135deg, #04140a 0%, #0b1f12 50%, #04140a 100%)",
        }}
      >
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: "radial-gradient(circle at 25% 35%, white 1px, transparent 1px), radial-gradient(circle at 75% 65%, white 1px, transparent 1px)",
          backgroundSize: "44px 44px"
        }}/>
        <div className="relative">
          <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-[0.35em] font-medium">
            <Sparkles className="w-4 h-4"/>NOVA · Identificación de Retos
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-white mt-3 tracking-tight leading-[1]">
            Retos <span style={{background:"linear-gradient(135deg,#3BAD2D,#86efac)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>2026</span>
          </h1>
          <p className="text-emerald-100/80 mt-3 max-w-2xl text-base">
            Lanza campañas, recoge retos del equipo, hace matching con tu portfolio y descubre soluciones de mercado con IA.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-3 max-w-xl">
            <Kpi label="Retos identificados" value={totals.challenges} icon={<Target className="w-4 h-4"/>}/>
            <Kpi label="Invitados" value={totals.invitees} icon={<Users className="w-4 h-4"/>}/>
            <Kpi label="Respuestas públicas" value={totals.submissions} icon={<MessageSquare className="w-4 h-4"/>}/>
          </div>
          <div className="mt-7 flex gap-3">
            <Button
              onClick={() => setShowWizard(true)}
              data-testid="nova-create-btn"
              className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2 rounded-full px-5 py-5"
            >
              <Plus className="w-4 h-4"/> Lanzar nueva campaña
            </Button>
            <Button
              onClick={() => navigate("/nova/dashboard")}
              variant="secondary"
              data-testid="nova-dashboard-btn"
              className="gap-2 rounded-full px-5 py-5 bg-white/10 hover:bg-white/15 text-white border border-white/20"
            >
              <BarChart3 className="w-4 h-4"/> Dashboard & Leaderboard
            </Button>
            <Button
              onClick={() => navigate("/nova/consolidado")}
              variant="secondary"
              data-testid="nova-consolidated-btn"
              className="gap-2 rounded-full px-5 py-5 bg-white/10 hover:bg-white/15 text-white border border-white/20"
            >
              <Layers className="w-4 h-4"/> Comité consolidado
            </Button>
            <Button
              onClick={() => navigate("/nova/manual")}
              variant="secondary"
              data-testid="nova-manual-btn"
              className="gap-2 rounded-full px-5 py-5 bg-white/10 hover:bg-white/15 text-white border border-white/20"
            >
              <BookOpen className="w-4 h-4"/> Manual NOVA
            </Button>
          </div>
        </div>
      </div>

      {/* Campaigns grid */}
      <div>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-xl font-bold">Campañas activas</h2>
          <span className="text-xs text-muted-foreground">{campaigns.length} campañas</span>
        </div>
        {loading ? (
          <div className="text-muted-foreground text-sm">Cargando…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {campaigns.map((c) => {
              const theme = AREA_THEMES[c.area] || AREA_THEMES["Servicios Urbanos"];
              return (
                <motion.button
                  key={c.id}
                  whileHover={{ y: -4 }}
                  onClick={() => navigate(`/nova/${c.id}`)}
                  data-testid={`nova-campaign-${c.id}`}
                  className="relative text-left rounded-2xl p-6 overflow-hidden group"
                  style={{
                    background: `linear-gradient(135deg, ${theme.from}22, ${theme.to}DD)`,
                    border: `1px solid ${theme.accent}44`,
                  }}
                >
                  <div className="absolute -right-6 -top-6 text-7xl opacity-15 group-hover:opacity-25 transition-opacity">
                    {theme.emoji}
                  </div>
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">
                      {c.area} · {c.year}
                    </div>
                    <h3 className="text-xl font-bold text-white mt-2 leading-tight">
                      {c.name}
                    </h3>
                    <p className="text-white/70 text-sm mt-2 line-clamp-3 min-h-[60px]">
                      {c.description}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(c.tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/80 border border-white/15">
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="mt-5 flex items-center justify-between text-white/85">
                      <div className="flex gap-4 text-xs">
                        <span><strong>{c.challenges_count}</strong> retos</span>
                        <span><strong>{c.invitees_count}</strong> invitados</span>
                        <span><strong>{c.submissions_count}</strong> respuestas</span>
                      </div>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </div>

      {showWizard && <CampaignWizard token={token} onClose={() => setShowWizard(false)} onCreated={(c) => navigate(`/nova/${c.id}`)} />}
    </div>
  );
}

function Kpi({ label, value, icon }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 backdrop-blur">
      <div className="flex items-center gap-1.5 text-emerald-300 text-[10px] uppercase tracking-wider font-semibold">
        {icon}{label}
      </div>
      <div className="text-2xl font-black text-white mt-0.5">{value}</div>
    </div>
  );
}

function CampaignWizard({ token, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [area, setArea] = useState("Servicios Urbanos");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [sessionDate, setSessionDate] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name,
          area,
          year: 2026,
          description,
          tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
          session_date: sessionDate || null,
          public_password: password || null,
        }),
      });
      const c = await r.json();
      onCreated(c);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="nova-wizard">
      <div className="bg-card rounded-2xl border border-border w-full max-w-xl p-6 space-y-4">
        <h2 className="text-xl font-bold">Nueva campaña NOVA</h2>
        <Field label="Nombre"><input className="w-full px-3 py-2 rounded-lg border border-border bg-background" value={name} onChange={(e)=>setName(e.target.value)} data-testid="wiz-name" placeholder="Identificación de Retos 2026 · Mantenimiento"/></Field>
        <Field label="Área">
          <select className="w-full px-3 py-2 rounded-lg border border-border bg-background" value={area} onChange={(e)=>setArea(e.target.value)} data-testid="wiz-area">
            <option>Servicios Urbanos</option>
            <option>Tratamiento Operaciones</option>
            <option>Tratamiento Privados</option>
            <option>Transversal</option>
            <option>Otro</option>
          </select>
        </Field>
        <Field label="Descripción"><textarea rows={3} className="w-full px-3 py-2 rounded-lg border border-border bg-background" value={description} onChange={(e)=>setDescription(e.target.value)} placeholder="Contexto y objetivo de la sesión"/></Field>
        <Field label="Tags (coma)"><input className="w-full px-3 py-2 rounded-lg border border-border bg-background" value={tags} onChange={(e)=>setTags(e.target.value)} placeholder="Ej: PRL, Bioestabilizado, Maquinaria"/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fecha de la sesión"><input type="date" className="w-full px-3 py-2 rounded-lg border border-border bg-background" value={sessionDate} onChange={(e)=>setSessionDate(e.target.value)}/></Field>
          <Field label="Contraseña pública (opcional)"><input className="w-full px-3 py-2 rounded-lg border border-border bg-background" value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="Se genera si la dejas vacía"/></Field>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-border">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={submitting || !name} data-testid="wiz-submit" className="bg-emerald-500 hover:bg-emerald-600 text-white">
            {submitting ? "Creando…" : "Crear campaña"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
